import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, CheckCircle, MessageSquare } from 'lucide-react';
import { ReviewService, ReviewWithUser } from '@/lib/reviewService';
import { ReviewTargetType } from '@/types/reviews';
import { formatDistanceToNow } from 'date-fns';

interface RecentReviewsHighlightProps {
  targetType: ReviewTargetType;
  targetId: string;
  maxReviews?: number;
  showTitle?: boolean;
  className?: string;
}

const RecentReviewsHighlight: React.FC<RecentReviewsHighlightProps> = ({
  targetType,
  targetId,
  maxReviews = 3,
  showTitle = true,
  className = ''
}) => {
  const [reviews, setReviews] = useState<ReviewWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecentReviews = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch recent reviews with limited count
        const recentReviews = await ReviewService.getReviews(targetType, targetId, {
          limit: maxReviews,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });
        
        setReviews(recentReviews);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load recent reviews');
      } finally {
        setIsLoading(false);
      }
    };

    if (targetId) {
      fetchRecentReviews();
    }
  }, [targetType, targetId, maxReviews]);

  const getUserDisplayName = (user: any) => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return 'Anonymous User';
  };

  const getUserInitials = (user: any) => {
    const name = getUserDisplayName(user);
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        {showTitle && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Recent Reviews
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-4">
            {[...Array(maxReviews)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || reviews.length === 0) {
    return (
      <Card className={className}>
        {showTitle && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Recent Reviews
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>{error || 'No reviews yet'}</p>
            <p className="text-sm">{!error && 'Be the first to review!'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {showTitle && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Recent Reviews
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="flex gap-3">
              <Avatar className="w-8 h-8">
                <AvatarImage src={review.user?.avatar_url} />
                <AvatarFallback className="text-xs">
                  {getUserInitials(review.user)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm truncate">
                    {getUserDisplayName(review.user)}
                  </span>
                  {review.verified_purchase && (
                    <Badge variant="secondary" className="text-xs px-1 py-0">
                      <CheckCircle className="w-2 h-2 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-3 h-3 ${
                          star <= review.rating 
                            ? 'fill-yellow-400 text-yellow-400' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                  </span>
                </div>
                
                {review.title && (
                  <h4 className="font-medium text-sm mb-1 truncate">
                    {review.title}
                  </h4>
                )}
                
                {review.content && (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {review.content}
                  </p>
                )}
                
                {review.recommended !== undefined && (
                  <div className="mt-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                      review.recommended 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {review.recommended ? '👍 Recommends' : '👎 Doesn\'t recommend'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentReviewsHighlight; 